'use client';

import { CompositeItem, useMergeRefs } from '@floating-ui/react';
import { animated, easings, useReducedMotion, useSpring } from '@react-spring/web';
import { IconChevronDownFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-down-filled';
import { interactionResponse } from '@zillow/yield-callback';
import { useRef } from 'react';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useInteractiveElementKeyDownCallback } from '../../hooks/use-interactive-element-key-down-callback';
import { useSlotContent } from '../../hooks/use-slot-content';
import { Box } from '../box';
import { Icon } from '../icon';
import { Slot } from '../slot';
import { AccordionItemContext } from './context/accordion-item.context';
import { AccordionRootContext } from './context/accordion-root.context';
import { accordionHeaderRecipe } from './styles/accordion-header.styles';
import type { AccordionHeaderPropsInterface } from './types/accordion-header.types';

/**
 * Interactive heading text that hides or reveals the content in its sibling
 * `Accordion.Panel`.
 *
 * @since 8.43.0
 */

export const AccordionHeader = forwardRef<HTMLDivElement, AccordionHeaderPropsInterface>(
  (props: AccordionHeaderPropsInterface, forwardedRef) => {
    const { asChild = false, children, className, css: cssProp, onClick, ...rest } = props;

    const reduceMotion = useReducedMotion();

    const { disabled, expanded, value } = useContext(AccordionItemContext);
    const { toggleExpanded, shouldAwaitInteractionResponse } = useContext(AccordionRootContext);

    const styles = css(accordionHeaderRecipe.raw({ expanded, disabled }), cssProp);

    const onAccordionHeaderClick: MouseEventHandler<HTMLDivElement> = useCallback(
      async (event) => {
        if (disabled) return;

        if (shouldAwaitInteractionResponse) {
          await interactionResponse();
        }

        toggleExpanded?.(value);
        onClick?.(event);
      },
      [disabled, onClick, value, toggleExpanded, shouldAwaitInteractionResponse],
    );

    const animatedIconStyles = css({
      display: 'inline-flex',
    });

    const animatedIconProps = useSpring({
      rotate: expanded ? 180 : 0,
      config: {
        easing: easings.easeInOutQuart,
        duration: reduceMotion ? 0 : 250,
      },
    });

    const accordionHeaderRef = useRef<HTMLDivElement>(null);
    const refs = useMergeRefs([accordionHeaderRef, forwardedRef]);

    const onKeyDownCallback = useInteractiveElementKeyDownCallback({
      ref: accordionHeaderRef,
      onClick: onAccordionHeaderClick,
    });

    const Component = asChild ? Slot : 'div';

    const content = useSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <>
          <animated.span className={cx('c11n', animatedIconStyles)} style={animatedIconProps}>
            <Icon render={<IconChevronDownFilled />} />
          </animated.span>
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '{spacing.layout.default}',
              flex: 1,
            }}
          >
            {childContent}
          </Box>
        </>
      ),
      dependencies: [animatedIconStyles, animatedIconProps],
    });

    return (
      <CompositeItem
        render={() => (
          <Component
            // eslint-disable-next-line jsx-a11y(prefer-tag-over-role)
            role="button"
            ref={refs}
            id={`${value}-header`}
            onClick={onAccordionHeaderClick}
            onKeyDown={onKeyDownCallback}
            aria-expanded={expanded}
            aria-controls={`${value}-panel`}
            aria-disabled={disabled}
            data-c11n-component="Accordion.Header"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', styles, className)}
            tabIndex={0}
            {...rest}
          >
            {content}
          </Component>
        )}
      />
    );
  },
);

AccordionHeader.displayName = 'Accordion.Header';
